import { useState } from "react";
import { ClipboardList, Loader2, X } from "lucide-react";
import { useUiStore } from "../../stores/uiStore";
import { useWorkOrders, type WorkOrderRow } from "../../hooks/queries";
import { Modal } from "../../ui/Modal";
import { TargetResultsUpload } from "../target-results/TargetResultsUpload";
import {
  UploadResultNote,
  type UploadResult,
} from "../target-results/UploadResultNote";
import {
  canVisualizeOrder,
  labResultsActionLabel,
  labUploadTarget,
  workOrderStatusLabel,
  type LabUploadTarget,
} from "./labResults";
import { useWorkOrderResultPoll } from "./useWorkOrderResultPoll";

// Work orders: soil-sampling and EC-scan orders. A sampling row can attach a lab
// file; a point file finishes on the ingest job, so that order is polled until
// its status is completed.

// EC scan patterns as the user saw them on the order buttons; unknown values pass through
// raw rather than hiding what the backend recorded.
const EC_PATTERN_LABEL: Record<string, string> = {
  single_line: "Single-line",
  adaptive: "Adaptive frequency",
  dual_line: "Dual-line",
};

function typeLabel(o: WorkOrderRow): string {
  if (o.kind === "ec")
    return `EC scan${o.detail ? ` — ${EC_PATTERN_LABEL[o.detail] ?? o.detail}` : ""}`;
  return `Soil sampling${o.detail ? ` — ${o.detail}` : ""}`;
}

function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// Business-status badge; unknown statuses render neutrally rather than breaking (the
// status vocabulary is the backend's, not ours). "completed" reads "Complete".
function statusBadge(status: string) {
  const s = status.toLowerCase();
  const cls = /complete|collected|received|done/.test(s)
    ? "bg-emerald-400/15 text-emerald-300"
    : /fail|cancel|reject/.test(s)
      ? "bg-red-400/15 text-red-300"
      : /schedul|assign|progress|route/.test(s)
        ? "bg-sky-400/15 text-sky-300"
        : "bg-amber-400/15 text-amber-300"; // submitted / pending / anything new
  return (
    <span className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${cls}`}>
      {workOrderStatusLabel(status)}
    </span>
  );
}

function visualizeOrder(row: WorkOrderRow) {
  const sampleEventId =
    row.results?.sample_event_id || row.design_event_id || "";
  const ui = useUiStore.getState();
  ui.openMap(row.field_id);
  if (sampleEventId)
    ui.setPendingLayerFocus({
      fieldId: row.field_id,
      sampleEventId,
      prefer: ["ph", "om"],
    });
  ui.closeWorkOrders();
}

function WorkOrderActions({
  row,
  onUpload,
}: {
  row: WorkOrderRow;
  onUpload: (row: WorkOrderRow) => void;
}) {
  const upload = labResultsActionLabel(row);
  const visualize = canVisualizeOrder(row);
  if (!upload && !visualize) return null;
  return (
    <div className="flex flex-wrap items-center gap-2">
      {upload && (
        <button
          type="button"
          className="rounded border border-line px-2 py-0.5 text-[11px] text-fg hover:bg-hover"
          onClick={() => onUpload(row)}
        >
          {upload}
        </button>
      )}
      {visualize && (
        <button
          type="button"
          className="text-[11px] text-sky-300 underline hover:text-sky-200"
          onClick={() => visualizeOrder(row)}
        >
          View in Visualize
        </button>
      )}
    </div>
  );
}

function WorkOrderTable({
  rows,
  onUpload,
}: {
  rows: WorkOrderRow[];
  onUpload: (row: WorkOrderRow) => void;
}) {
  return (
    <table className="w-full border-collapse text-xs">
      <thead>
        <tr className="border-b border-line text-left text-[11px] text-muted">
          <th className="py-1.5 pr-3 font-medium">Field</th>
          <th className="py-1.5 pr-3 font-medium">Type</th>
          <th className="py-1.5 pr-3 font-medium">Lab &amp; order options</th>
          <th className="py-1.5 pr-3 font-medium">Ordered</th>
          <th className="py-1.5 pr-3 font-medium">Points</th>
          <th className="py-1.5 pr-3 font-medium">Status</th>
          <th className="py-1.5 pr-3 font-medium">Actions</th>
        </tr>
      </thead>
      <tbody>
        {rows.map((o) => (
          <tr
            key={`${o.kind}-${o.request_id}`}
            className="border-b border-line/50"
          >
            <td className="py-2 pr-3 text-fg">{o.field_name}</td>
            <td className="py-2 pr-3 text-muted">{typeLabel(o)}</td>
            <td className="py-2 pr-3 text-muted">{o.lab ?? "—"}</td>
            <td className="tnum py-2 pr-3 text-muted">
              {fmtDate(o.created_at)}
            </td>
            <td className="tnum py-2 pr-3 text-muted">
              {o.point_count ?? "—"}
            </td>
            <td className="py-2 pr-3">{statusBadge(o.status)}</td>
            <td className="py-2 pr-3">
              <WorkOrderActions row={o} onUpload={onUpload} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function OrderList({
  data,
  isLoading,
  isError,
  onUpload,
}: {
  data: WorkOrderRow[] | undefined;
  isLoading: boolean;
  isError: boolean;
  onUpload: (row: WorkOrderRow) => void;
}) {
  if (isLoading)
    return (
      <div className="flex items-center gap-2 text-xs text-faint">
        <Loader2 size={13} className="animate-spin" /> Loading orders…
      </div>
    );
  if (isError)
    return (
      <div className="text-xs text-red-400">
        Could not load work orders — try again in a moment.
      </div>
    );
  if (data && data.length === 0)
    return (
      <div className="text-xs text-faint">
        No sampling orders yet. Place points from a field&apos;s Soil samples
        options (&ldquo;Create points &amp; order sampling&rdquo;) and
        they&apos;ll show up here.
      </div>
    );
  if (data && data.length > 0)
    return <WorkOrderTable rows={data} onUpload={onUpload} />;
  return null;
}

export function WorkOrdersPage() {
  const close = useUiStore((s) => s.closeWorkOrders);
  const { data, isLoading, isError } = useWorkOrders(true);
  const [upload, setUpload] = useState<LabUploadTarget | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [uploadNote, setUploadNote] = useState<UploadResult | null>(null);
  const dismissUploadNotice = () =>
    setUploadNote((note) => (note ? { ...note, notice: null } : note));
  const [pollingId, setPollingId] = useState<string | null>(null);
  useWorkOrderResultPoll(pollingId);

  const openUpload = (row: WorkOrderRow) => {
    setUploadError(null);
    void labUploadTarget(row).then(setUpload, (e: unknown) => {
      setUploadError(
        e instanceof Error ? e.message : "Could not open the lab upload.",
      );
    });
  };

  return (
    <>
      <Modal>
        <div className="fixed inset-0 z-dialog flex flex-col bg-surface text-fg sm:inset-8 sm:rounded-lg sm:shadow-2xl">
          <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
            <h2 className="flex items-center gap-2 text-sm font-semibold">
              <ClipboardList size={15} /> Work orders
            </h2>
            <button
              className="rounded p-1 text-muted hover:bg-hover"
              onClick={close}
              aria-label="Close work orders"
            >
              <X size={16} />
            </button>
          </div>

          <div className="min-h-0 flex-1 overflow-auto p-4">
            <OrderList
              data={data}
              isLoading={isLoading}
              isError={isError}
              onUpload={openUpload}
            />
            {uploadError && (
              <p role="alert" className="mt-2 text-xs text-red-400">
                {uploadError}
              </p>
            )}
            <UploadResultNote
              result={uploadNote}
              onDismissNotice={dismissUploadNotice}
            />
          </div>
        </div>
      </Modal>
      {upload && (
        <TargetResultsUpload
          fieldId={upload.fieldId}
          layerId={upload.layerId}
          title={upload.title}
          kind={upload.kind}
          fc={upload.fc}
          workOrderId={upload.workOrderId}
          onUploaded={setUploadNote}
          onClose={() => setUpload(null)}
          onPointsProcessing={() => setPollingId(upload.workOrderId)}
        />
      )}
    </>
  );
}
